import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useClubById } from '../../../hooks/clubs/useClubById'

import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import {
  ClubEdit,
  ClubEditActions,
} from '../../../components/authenticated/management/club/clubEdit/ClubEdit'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ClubModel } from '../../../types/court/court'

export const ManagerClub = () => {
  const navigate = useNavigate()
  const { clubId } = useParams()
  const { showBanner } = useApp()
  const { t } = useTranslation('clubs')
  const [club, setClub] = useState<ClubModel | null>(null)
  const [editorActions, setEditorActions] = useState<ClubEditActions | null>(
    null
  )

  const isEditMode = Boolean(clubId)

  const {
    clubById,
    error: clubError,
    resetState: resetClubState,
  } = useClubById()

  useEffect(() => {
    const loadData = async () => {
      setClub(null)
      setEditorActions(null)
      const parsedClubId = clubId ? Number(clubId) : null

      if (
        parsedClubId !== null &&
        (!Number.isInteger(parsedClubId) || parsedClubId <= 0)
      ) {
        navigate('/m/clubs', { replace: true })
        return
      }

      const clubData =
        parsedClubId !== null ? await clubById(parsedClubId) : null

      if (parsedClubId !== null && !clubData) {
        navigate('/m/clubs', { replace: true })
        return
      }

      if (clubData) {
        setClub(clubData)
      }
    }

    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId])

  useEffect(() => {
    if (!clubError) {
      return
    }

    showBanner({
      kind: BannerKind.ERR,
      msg: clubError,
    })

    resetState()
    navigate('/m/clubs', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubError])

  const resetState = () => {
    resetClubState()
  }

  const handleBack = () => {
    navigate('/m/clubs')
  }

  const handleEditorActionsChange = useCallback(
    (actions: ClubEditActions) => setEditorActions(actions),
    []
  )

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t(
            isEditMode
              ? 'clubs.edit.page-header.eyebrow'
              : 'clubs.new.page-header.eyebrow'
          ),
          h1:
            club?.name ??
            t(
              isEditMode
                ? 'clubs.edit.page-header.title'
                : 'clubs.new.page-header.title'
            ),
        }}
        sub={t(
          isEditMode
            ? 'clubs.edit.page-header.subtitle'
            : 'clubs.new.page-header.subtitle'
        )}
        actions={[
          <Button
            key="back"
            label={t('clubs.edit.page-header.back-btn')}
            size="md"
            variant="ghost"
            onClick={handleBack}
          />,
          <Button
            key="reset"
            label={t('clubs.form.reset-btn')}
            size="md"
            variant="dark"
            disabled={!editorActions?.isDirty || editorActions.loading}
            onClick={editorActions?.reset}
          />,
          <Button
            key="save"
            label={t(
              isEditMode ? 'clubs.edit.save-btn' : 'clubs.new.save-btn'
            )}
            size="md"
            disabled={
              !editorActions?.isDirty || !editorActions.isValid
            }
            onClick={editorActions?.save}
          />,
        ]}
      />
      {isEditMode && !club ? (
        <Loading variant="card" />
      ) : (
        <ClubEdit
          club={club}
          isEditMode={isEditMode}
          onActionsChange={handleEditorActionsChange}
        />
      )}
    </>
  )
}
